import classNames from 'classnames'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { useGetGiftPackages } from '../../../../../hooks/queries/gifts/use-get-gift-packages'
import { useGetGifts } from '../../../../../hooks/queries/gifts/use-get-gifts'

import { useListenerMessage } from '../../../../../hooks/subscription/use-listener-message'
import { useGlobalStore } from '../../../../../store/global-store'
import { Loading } from '../loading'
import { Gift } from './components/gift'

interface Props {
  isPackageSelected: boolean
  token?: string | null
}

// grid horizontal paginado: cada página = 2 linhas × 4 colunas (8 gifts). Rola na
// horizontal com snap; os "· · ·" abaixo indicam a página atual.
const PER_PAGE = 8

export function GiftsContainer({ isPackageSelected, token }: Props) {
  const { selectedType } = useGlobalStore(
    useShallow((state) => ({
      selectedType: state.selectedType,
    })),
  )

  const {
    gifts,
    isLoading: isLoadingGifts,
    hasNextPage: hasNextPageGift,
    nextPage: nextPageGift,
  } = useGetGifts(
    // is_package NUNCA aparece na lista normal — só na aba de packages quando
    // comprado (quantity>0, via useGetGiftPackages). Sem isto, um gift is_package=1
    // vazava pros normais e falhava no envio (roteava pro caminho de package sem
    // posse → "Gift insuficiente"/shop-not-package). Backend: g.is_package = 0.
    { giftTypeId: selectedType?.id, isPackage: false },
    { enabled: !isPackageSelected && Boolean(selectedType?.id), token },
  )

  const {
    gifts: giftsInPackageTypes,
    isLoading: isLoadingPackage,
    hasNextPage: hasNextPagePackage,
    nextPage: nextPagePackage,
    refetch,
  } = useGetGiftPackages({ enabled: isPackageSelected, token })

  const isLoading = isPackageSelected ? isLoadingPackage : isLoadingGifts

  const allGifts = useMemo(() => {
    return isPackageSelected ? giftsInPackageTypes : gifts
  }, [isPackageSelected, giftsInPackageTypes, gifts])

  const hasNextPage = isPackageSelected ? hasNextPagePackage : hasNextPageGift
  const loadMore = isPackageSelected ? nextPagePackage : nextPageGift

  // PREFETCH: carrega TODAS as páginas assim que monta. A webview do modal já é
  // pré-renderizada ao ENTRAR NA LIVE (ModalCustom shouldPreRender), então quando o
  // usuário abrir os presentes tudo já está carregado — sem esperar scroll. Dispara
  // loadMore enquanto houver próxima página; allGifts.length muda a cada página
  // carregada e re-roda o efeito até acabar (fetchNextPage é no-op se já buscando).
  useEffect(() => {
    if (hasNextPage) loadMore()
  }, [hasNextPage, allGifts?.length, loadMore])

  // fatia os gifts em páginas de 8 (2×4)
  const pages = useMemo(() => {
    const arr = allGifts ?? []
    const out: (typeof arr)[] = []
    for (let i = 0; i < arr.length; i += PER_PAGE) out.push(arr.slice(i, i + PER_PAGE))
    return out
  }, [allGifts])

  const scrollRef = useRef<HTMLDivElement>(null)
  const [page, setPage] = useState(0)

  const onScroll = () => {
    const el = scrollRef.current
    if (!el) return
    const p = Math.round(el.scrollLeft / el.clientWidth)
    if (p !== page) setPage(p)
    // lazy-load ao chegar perto do fim (na horizontal)
    if (hasNextPage && el.scrollLeft + el.clientWidth >= el.scrollWidth - 24) {
      loadMore()
    }
  }

  useListenerMessage((data) => {
    if (data === 'load-gifts-packages') {
      refetch()
    }
  })

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="flex-1 flex overflow-x-auto overflow-y-hidden snap-x snap-mandatory [&::-webkit-scrollbar]:hidden"
        style={{ scrollbarWidth: 'none' }}
      >
        {isLoading && <Loading />}

        {/* h-full + rows 1fr (grid-rows-2): as 2 linhas dividem a altura
            disponível — em devices baixos o tile encolhe em vez de cortar o
            valor do gift na linha de baixo */}
        {pages.map((pageGifts, idx) => (
          <div
            key={idx}
            className="w-full h-full flex-none snap-start grid grid-cols-4 grid-rows-2 gap-x-2 gap-y-1 pt-2"
          >
            {pageGifts.map((gift) => (
              <Gift
                key={gift.id}
                isLocked={selectedType?.isLocked ?? false}
                gift={gift}
              />
            ))}
          </div>
        ))}
      </div>

      {/* indicador de páginas: · · · */}
      {pages.length > 1 && (
        <div className="flex justify-center items-center gap-1.5 py-2">
          {pages.map((_, i) => (
            <span
              key={i}
              className={classNames('rounded-full transition-all duration-200', {
                'w-2 h-2 bg-white': i === page,
                'w-1.5 h-1.5 bg-white/30': i !== page,
              })}
            />
          ))}
        </div>
      )}
    </div>
  )
}
